import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Package, MapPin, Clock, CheckCircle, Truck, Phone, User, AlertCircle, X, Download } from 'lucide-react';
import { contactInfo } from '../mock';
import axios from 'axios';

export const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [trackingId, setTrackingId] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null); // For photo modal

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-track when a tracking ID is passed via URL (e.g. /track?id=AIS123456)
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl && idFromUrl.trim()) {
      const cleanId = idFromUrl.trim().toUpperCase();
      setTrackingId(cleanId);
      // trigger lookup immediately
      lookupTracking(cleanId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const lookupTracking = async (idValue) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${idValue.toUpperCase()}`
      );
      if (response.data.success) {
        setTrackingResult(response.data.data);
      } else {
        setTrackingResult('not_found');
      }
    } catch (error) {
      const result = mockTrackingData[idValue.toUpperCase()];
      if (result) {
        setTrackingResult(result);
      } else {
        setTrackingResult('not_found');
      }
    } finally {
      setLoading(false);
    }
  };

  // Mock tracking data (you can later replace with real API)
  const mockTrackingData = {
    'AIS001234': {
      id: 'AIS001234',
      status: 'In Transit',
      customerName: 'Sarah Johnson',
      company: 'TechStart Ltd',
      pickup: {
        address: '123 Business Park, Reading, RG1 2AB',
        time: '09:30 AM',
        date: 'Today',
        completed: true
      },
      delivery: {
        address: '456 Commercial Center, Slough, SL1 3CD',
        estimatedTime: '02:30 PM',
        date: 'Today',
        completed: false
      },
      driver: {
        name: 'James Wilson',
        phone: '+44 7123 456789',
        vehicle: 'Large Van - AIS 04',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      },
      timeline: [
        {
          status: 'Booking Confirmed',
          time: '08:45 AM',
          description: 'Your delivery has been scheduled and confirmed',
          completed: true
        },
        {
          status: 'Driver Assigned',
          time: '09:15 AM', 
          description: 'James Wilson has been assigned to your delivery',
          completed: true
        },
        {
          status: 'Package Collected',
          time: '09:30 AM',
          description: 'Package collected from pickup location',
          completed: true
        },
        {
          status: 'In Transit',
          time: '10:15 AM',
          description: 'Package is on route to delivery address',
          completed: true,
          current: true
        },
        {
          status: 'Out for Delivery',
          time: 'Est. 02:00 PM',
          description: 'Driver is in your local area for final delivery',
          completed: false
        },
        {
          status: 'Delivered',
          time: 'Est. 02:30 PM',
          description: 'Package delivered successfully',
          completed: false
        }
      ]
    },
    'AIS005678': {
      id: 'AIS005678',
      status: 'Delivered',
      customerName: 'Michael Chen',
      company: 'Creative Solutions',
      pickup: {
        address: '789 Office Complex, Bracknell, RG12 1EF',
        time: '11:00 AM',
        date: 'Today',
        completed: true
      },
      delivery: {
        address: '321 Industrial Estate, Windsor, SL4 2GH',
        estimatedTime: '01:45 PM',
        date: 'Today',
        completed: true
      },
      driver: {
        name: 'Sarah Mitchell',
        phone: '+44 7987 654321',
        vehicle: 'Small Van - AIS 02',
        image: 'https://images.unsplash.com/photo-1494790108755-2616c6d31c42?w=150&h=150&fit=crop&crop=face'
      },
      timeline: [
        {
          status: 'Booking Confirmed',
          time: '10:30 AM',
          description: 'Your delivery has been scheduled and confirmed',
          completed: true
        },
        {
          status: 'Driver Assigned',
          time: '10:45 AM',
          description: 'Sarah Mitchell has been assigned to your delivery',
          completed: true
        },
        {
          status: 'Package Collected',
          time: '11:00 AM',
          description: 'Package collected from pickup location',
          completed: true
        },
        {
          status: 'In Transit',
          time: '11:30 AM',
          description: 'Package is on route to delivery address',
          completed: true
        },
        {
          status: 'Out for Delivery',
          time: '01:20 PM',
          description: 'Driver arrived in your local area',
          completed: true
        },
        {
          status: 'Delivered',
          time: '01:45 PM',
          description: 'Package delivered successfully to reception desk',
          completed: true,
          current: true
        }
      ]
    },
    'AIS009876': {
      id: 'AIS009876',
      status: 'Scheduled',
      customerName: 'Emma Williams',
      company: 'Fashion Forward',
      pickup: {
        address: '555 Retail Park, Basingstoke, RG21 4AA',
        time: '03:00 PM',
        date: 'Tomorrow',
        completed: false
      },
      delivery: {
        address: '777 Shopping Center, Farnborough, GU14 7LP',
        estimatedTime: '05:30 PM',
        date: 'Tomorrow',
        completed: false
      },
      driver: {
        name: 'To be assigned',
        phone: 'Will be provided',
        vehicle: 'XL Van - Pending',
        image: null
      },
      timeline: [
        {
          status: 'Booking Confirmed',
          time: '02:15 PM',
          description: 'Your scheduled delivery has been confirmed',
          completed: true,
          current: true
        },
        {
          status: 'Driver Assignment',
          time: 'Tomorrow 02:30 PM',
          description: 'Driver will be assigned 30 minutes before pickup',
          completed: false
        },
        {
          status: 'Package Collection',
          time: 'Tomorrow 03:00 PM',
          description: 'Scheduled pickup from your location',
          completed: false
        },
        {
          status: 'Delivery',
          time: 'Tomorrow 05:30 PM',
          description: 'Scheduled delivery to destination',
          completed: false
        }
      ]
    }
  };

  const handleTrackDelivery = async () => {
    if (!trackingId.trim()) {
      alert('Please enter a tracking ID');
      return;
    }
    await lookupTracking(trackingId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'text-green-600 bg-green-100';
      case 'In Transit':
      case 'Out for Delivery':
        return 'text-blue-600 bg-blue-100';
      case 'Scheduled':
        return 'text-purple-600 bg-purple-100';
      case 'Package Collected':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const resetTracking = () => {
    setTrackingId('');
    setTrackingResult(null);
  };

  return (
    <section className="min-h-screen py-12 bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-6">
            <Package className="h-4 w-4 mr-2" />
            <span>Delivery Tracking</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Track Your Delivery
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Enter your tracking ID below to get real-time updates on your AIS Express delivery status, 
            driver information, and estimated arrival time.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Tracking Input */}
          <Card className="mb-8 shadow-xl border-0 bg-white">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Enter Tracking ID
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      placeholder="e.g., AIS001234"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleTrackDelivery()}
                      className="pl-12 py-4 text-lg font-mono tracking-wider uppercase"
                    />
                  </div>
                </div>
                <div className="flex items-end space-x-3">
                  <Button 
                    onClick={handleTrackDelivery}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 text-lg font-semibold"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Tracking...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Search className="h-5 w-5" />
                        <span>Track</span>
                      </div>
                    )}
                  </Button>
                  
                  {trackingResult && (
                    <Button 
                      onClick={resetTracking}
                      variant="outline"
                      className="px-6 py-4"
                    >
                      New Search
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Demo IDs */}
          {!trackingResult && (
            <div className="text-center mb-8">
              <p className="text-sm text-slate-600 mb-3">Try these demo tracking IDs:</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button 
                  onClick={() => setTrackingId('AIS001234')}
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-mono hover:bg-emerald-200 transition-colors"
                >
                  AIS001234
                </button>
                <button 
                  onClick={() => setTrackingId('AIS005678')}
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-mono hover:bg-emerald-200 transition-colors"
                >
                  AIS005678
                </button>
                <button 
                  onClick={() => setTrackingId('AIS009876')}
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-mono hover:bg-emerald-200 transition-colors"
                >
                  AIS009876
                </button>
              </div>
            </div>
          )}

          {/* Tracking Results */}
          {trackingResult === 'not_found' && (
            <Card className="shadow-xl border-0 bg-white">
              <CardContent className="p-12 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100 mx-auto mb-6">
                  <AlertCircle className="h-10 w-10 text-red-600" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2">
                  Tracking ID Not Found
                </h3>
                <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                  We couldn't find a delivery with this tracking ID. Please check the number and try again, 
                  or contact our customer service team for assistance.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => window.open(`tel:${contactInfo.phone}`, '_self')}
                    className="bg-emerald-600 hover:bg-emerald-700 flex items-center space-x-2"
                  >
                    <Phone className="h-4 w-4" />
                    <span>Call {contactInfo.phone}</span>
                  </Button>
                  <Button 
                    onClick={resetTracking}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <Search className="h-4 w-4" />
                    <span>Try Another ID</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {trackingResult && trackingResult !== 'not_found' && (
            <div className="space-y-8">
              {/* Delivery Status Header */}
              <Card className="shadow-xl border-0 bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
                <CardContent className="p-8">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
                    <div>
                      <h2 className="text-3xl font-bold mb-2">
                        Tracking ID: {trackingResult.id}
                      </h2>
                      <div className="flex items-center space-x-4 text-emerald-100">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>{trackingResult.customerName}</span>
                        </div>
                        {trackingResult.company && (
                          <div>{trackingResult.company}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
                      <div className="text-center">
                        <div className={`text-2xl font-bold px-4 py-2 rounded-lg ${getStatusColor(trackingResult.status)}`}>
                          {trackingResult.status}
                        </div>
                        <div className="text-emerald-100 text-sm mt-2">Current Status</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Driver Information */}
              {trackingResult.driver.name !== 'To be assigned' && (
                <Card className="shadow-xl border-0 bg-white">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                      <Truck className="h-5 w-5 mr-2 text-emerald-600" />
                      Your Driver
                    </h3>
                    <div className="flex items-center space-x-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                        <User className="h-8 w-8 text-emerald-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 text-lg">{trackingResult.driver.name}</div>
                        <div className="text-slate-600">{trackingResult.driver.vehicle}</div>
                        {trackingResult.status === 'In Transit' || trackingResult.status === 'Out for Delivery' ? (
                          <Button 
                            size="sm"
                            onClick={() => window.open(`tel:${trackingResult.driver.phone}`, '_self')}
                            className="mt-2 bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Phone className="h-3 w-3 mr-1" />
                            Contact Driver
                          </Button>
                        ) : (
                          <div className="text-sm text-slate-500 mt-1">Driver contact available during delivery</div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pickup and Delivery Locations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="shadow-xl border-0 bg-white">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-emerald-600" />
                      Pickup Location
                    </h3>
                    <div className="space-y-2">
                      <div className="text-slate-700">{trackingResult.pickup.address}</div>
                      {trackingResult.pickup.completed ? (
                        <div className="flex items-center space-x-2 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Collected at {trackingResult.pickup.time} on {trackingResult.pickup.date}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm">
                            Scheduled for {trackingResult.pickup.time} on {trackingResult.pickup.date}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-xl border-0 bg-white">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-teal-600" />
                      Delivery Location
                    </h3>
                    <div className="space-y-2">
                      <div className="text-slate-700">{trackingResult.delivery.address}</div>
                      {trackingResult.delivery.completed ? (
                        <div className="flex items-center space-x-2 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Delivered at {trackingResult.delivery.estimatedTime} on {trackingResult.delivery.date}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm">
                            Est. arrival: {trackingResult.delivery.estimatedTime} on {trackingResult.delivery.date}
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline */}
              <Card className="shadow-xl border-0 bg-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-emerald-600" />
                    Delivery Timeline
                  </h3>
                  
                  <div className="space-y-6">
                    {trackingResult.timeline.map((event, index) => (
                      <div key={index} className="flex items-start space-x-4">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                          event.completed 
                            ? event.current
                              ? 'bg-emerald-500 text-white ring-4 ring-emerald-200'
                              : 'bg-emerald-100 text-emerald-600'
                            : 'bg-slate-100 text-slate-400'
                        } flex-shrink-0`}>
                          {event.completed ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <Clock className="h-5 w-5" />
                          )}
                        </div>
                        
                        <div className={`flex-1 pb-6 ${
                          index !== trackingResult.timeline.length - 1 
                            ? 'border-l-2 border-slate-100 ml-5 pl-6' 
                            : ''
                        }`}>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                            <div className="flex-1">
                              <div className={`text-lg font-semibold mb-1 ${
                                event.current 
                                  ? 'text-emerald-600' 
                                  : event.completed 
                                    ? 'text-slate-900' 
                                    : 'text-slate-500'
                              }`}>
                                {event.status}
                                {event.current && (
                                  <span className="ml-3 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                                    Current Status
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-600 leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                            <div className="text-sm font-medium text-slate-500 mt-2 sm:mt-0 sm:ml-4">
                              {event.time}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Photos */}
              {trackingResult.delivery_photos && trackingResult.delivery_photos.length > 0 && (
                <Card className="shadow-xl border-0 bg-white">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
                      <Package className="h-5 w-5 mr-2 text-emerald-600" />
                      Delivery Photos
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {trackingResult.delivery_photos.map((photoUrl, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photoUrl}
                            alt={`Delivery photo ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg shadow-md group-hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => setSelectedPhoto({ url: photoUrl, index: index + 1 })}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                              Click to view full size
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 mt-4 text-center">
                      Photos taken at delivery location as proof of completion
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Photo Modal */}
              {selectedPhoto && (
                <div 
                  className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                  onClick={() => setSelectedPhoto(null)}
                >
                  <div className="relative max-w-6xl max-h-[90vh] w-full">
                    {/* Close Button */}
                    <button
                      onClick={() => setSelectedPhoto(null)}
                      className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10 shadow-lg"
                    >
                      <X className="h-6 w-6 text-slate-900" />
                    </button>
                    
                    {/* Download Button */}
                    <a
                      href={selectedPhoto.url}
                      download={`delivery-photo-${selectedPhoto.index}.jpg`}
                      className="absolute top-4 left-4 bg-emerald-600 text-white rounded-full p-2 hover:bg-emerald-700 transition-colors z-10 shadow-lg flex items-center space-x-2 px-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download className="h-5 w-5" />
                      <span className="font-medium">Download</span>
                    </a>
                    
                    {/* Image */}
                    <img
                      src={selectedPhoto.url}
                      alt={`Delivery photo ${selectedPhoto.index}`}
                      className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-2xl"
                      onClick={(e) => e.stopPropagation()}
                    />
                    
                    {/* Image Info */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 rounded-lg px-6 py-2 shadow-lg">
                      <p className="text-sm font-medium text-slate-900">
                        Photo {selectedPhoto.index} of {trackingResult.delivery_photos.length}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Support Section */}
              <Card className="shadow-xl border-0 bg-gradient-to-br from-slate-50 to-emerald-50">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    Need Help or Have Questions?
                  </h3>
                  <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                    Our customer service team is available 24/7 to assist with any questions about your delivery 
                    or to provide additional updates.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      onClick={() => window.open(`tel:${contactInfo.phone}`, '_self')}
                      className="bg-emerald-600 hover:bg-emerald-700 flex items-center space-x-2 px-6 py-3"
                    >
                      <Phone className="h-4 w-4" />
                      <span>Call Customer Service</span>
                    </Button>
                    <Button 
                      onClick={() => window.location.href = '/#contact'}
                      variant="outline"
                      className="flex items-center space-x-2 px-6 py-3"
                    >
                      <Package className="h-4 w-4" />
                      <span>Contact Us Online</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* How to Get Tracking ID */}
          {!trackingResult && (
            <Card className="mt-12 shadow-lg border-0 bg-white">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">
                  How to Get Your Tracking ID
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 mx-auto mb-3">
                      <Phone className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">1. Book Your Delivery</h4>
                    <p className="text-sm text-slate-600">Call us to book your delivery service</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 mx-auto mb-3">
                      <Package className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">2. Receive Tracking ID</h4>
                    <p className="text-sm text-slate-600">We'll provide your unique tracking ID via SMS</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 mx-auto mb-3">
                      <Search className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">3. Track Online</h4>
                    <p className="text-sm text-slate-600">Enter your ID here for live updates</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};